/**
 * Forgot Password Page
 */

import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { z } from "zod";
import { Link as RouterLink } from "react-router-dom";
import { useForgotPassword } from "@/hooks/useAuth";
import { getApiErrorMessage } from "@/utils/formatters";
import { getTenantSlugFromHostname } from "@/utils/tenant";
import { usePublicBranding } from "@/hooks/useTenants";
import { TenantBranding } from "@/components/common/TenantBranding";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useAppForm } from "@/hooks/useAppForm";
import { emailRequiredSchema } from "@/utils/schemas";

const forgotPasswordSchema = z.object({
  email: emailRequiredSchema,
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export const ForgotPasswordPage: React.FC = () => {
  // The error shows in the form's Alert; no toast on top of it.
  const forgotPasswordMutation = useForgotPassword({ toastErrors: false });
  const [emailSent, setEmailSent] = useState(false);
  const subdomainSlug = getTenantSlugFromHostname();

  const { data: branding } = usePublicBranding(subdomainSlug);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useAppForm<ForgotPasswordFormData>({
    schema: forgotPasswordSchema,
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    forgotPasswordMutation.mutate(
      { ...data, ...(subdomainSlug ? { tenant_slug: subdomainSlug } : {}) },
      {
        onSuccess: () => {
          setEmailSent(true);
        },
      }
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 450,
          width: "100%",
          boxShadow: 3,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Logo & Title */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Box sx={{ mb: 2 }}>
              <TenantBranding
                tenantName={branding?.name}
                size="medium"
                showTenantName={false}
                logoUrl={branding?.logo_url}
                primaryColor={branding?.primary_color}
                secondaryColor={branding?.secondary_color}
              />
            </Box>
            <Typography variant="h4" gutterBottom sx={{
              fontWeight: 700
            }}>
              Forgot Password?
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                textAlign: "center"
              }}>
              Enter your email address and we'll send you instructions to reset
              your password
            </Typography>
          </Box>

          {/* Success Message */}
          {emailSent && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Password reset email sent! Please check your inbox and follow the
              instructions.
            </Alert>
          )}

          {/* Error Alert */}
          {forgotPasswordMutation.isError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {getApiErrorMessage(
                forgotPasswordMutation.error,
                "Failed to send reset email. Please try again."
              )}
            </Alert>
          )}

          {!emailSent && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email Address"
                    type="email"
                    fullWidth
                    margin="normal"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    autoComplete="email"
                    autoFocus
                  />
                )}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={forgotPasswordMutation.isPending}
                sx={{
                  mt: 3,
                  mb: 2,
                  ...(subdomainSlug && branding?.primary_color
                    ? {
                        bgcolor: branding.primary_color,
                        "&:hover": { bgcolor: branding.primary_color, filter: "brightness(0.9)" },
                      }
                    : {}),
                }}
              >
                {forgotPasswordMutation.isPending ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Send Reset Link"
                )}
              </Button>
            </form>
          )}

          {/* Back to Login Link */}
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Link
              component={RouterLink}
              to="/login"
              variant="body2"
              underline="hover"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              <ArrowBackIcon fontSize="small" />
              Back to Sign In
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
